import type {
  RegistrationFinancialPaymentOffer,
  RegistrationFinancialStatus,
} from "./registration-financial-client";

/**
 * Module-memory holder for the PaymentIntent client secret of the offered
 * payment step (CLMS-117 T123 card-data scrub). Pattern precedent:
 * registration-financial-token-holder / first-credential-proof-holder —
 * React double-buffers fibers, so a secret placed in `useState` survives in
 * the `alternate` tree after a state "scrub" (Pay later, modal close); this
 * mutable module-level slot is never part of any fiber's hook state, so the
 * financial-status state (and its alternate fibers) never holds the secret.
 * Scope: the mounted payment modal receives the secret as a prop and its
 * Stripe Elements `options` (a `useMemo`) keep a copy in hook state until the
 * modal unmounts; after Pay later, a modal close, or a terminal status the
 * modal is unmounted, so no component state holds it. Nothing here ever
 * touches the URL, storage, history, caches, or logs.
 *
 * Reads and clears are gated on the owning `handoffId`, so a stale flow can
 * neither read nor clear another handoff's secret. The secret is retained
 * only for a response that renders the payment step; it is scrubbed on
 * Pay later / modal close, a confirmed payment, a terminal status, an
 * authority-lost revert, and flow unmount.
 */

let heldHandoffId: string | null = null;
let heldClientSecret: string | null = null;

/** The payment offer as the status state holds it: the secret itself is never in that state. */
export type HeldPaymentOffer = Omit<
  RegistrationFinancialPaymentOffer,
  "paymentIntentClientSecret"
> & {
  /** Whether the holder was given a client secret for this offer. */
  clientSecretHeld: boolean;
};

/** The status as the status state holds it (offer secret moved to the holder). */
export type HeldRegistrationFinancialStatus = Omit<RegistrationFinancialStatus, "payment"> & {
  payment: HeldPaymentOffer | null;
};

/** Non-consuming read, gated on the owning handoffId. */
export function readRegistrationPaymentClientSecret(handoffId: string): string | null {
  return heldHandoffId === handoffId ? heldClientSecret : null;
}

/** Synchronous handoff-scoped scrub: another handoff's clear is a no-op. */
export function clearRegistrationPaymentClientSecret(handoffId: string): void {
  if (heldHandoffId === handoffId) {
    heldHandoffId = null;
    heldClientSecret = null;
  }
}

/**
 * Splits a status response at the state boundary: the offer's client secret
 * (when `retainSecret` allows) parks in the holder and the returned status —
 * the form the financial-status state holds — carries just a non-secret
 * `clientSecretHeld` flag. Whenever the secret is NOT retained (no offer, no
 * secret, or `retainSecret` false) this handoff's slot is cleared, so a
 * previously parked secret cannot outlive a response that renders no payment
 * step.
 */
export function holdPaymentSecretAndRedact(
  handoffId: string,
  status: RegistrationFinancialStatus,
  retainSecret: boolean,
): HeldRegistrationFinancialStatus {
  const offer = status.payment;
  if (offer === null) {
    clearRegistrationPaymentClientSecret(handoffId);
    return { ...status, payment: null };
  }
  const { paymentIntentClientSecret, ...rest } = offer;
  const held = retainSecret && paymentIntentClientSecret !== null;
  if (held) {
    heldHandoffId = handoffId;
    heldClientSecret = paymentIntentClientSecret;
  } else {
    clearRegistrationPaymentClientSecret(handoffId);
  }
  return { ...status, payment: { ...rest, clientSecretHeld: held } };
}

/** Test-only unscoped peek so suites can assert holder contents. */
export function peekRegistrationPaymentClientSecretForTests(): string | null {
  return heldClientSecret;
}

/** Test-only unscoped reset for suite setup/teardown isolation. */
export function resetRegistrationPaymentSecretHolderForTests(): void {
  heldHandoffId = null;
  heldClientSecret = null;
}
